import { useEffect, useState } from "react";
import { getApiResponse } from "@src/utils/service";
import DownArrowIcon from "@icons/DownArrow";
import UpArrowIcon from "@icons/UpArrow";
import services from "@src/Services";
import CreateStoreDialog from "./CreateStoreDialog";
import CreateRevenueCenterDialog from "./CreateRevenueCenterDialog";

import {
  PageWrapper,
  PageTitle,
  PropertyContainer,
  HeaderWrapper,
  PropertyTitle,
  CreateStoreButton,
  StoreContainer,
  StoreHeaderWrapper,
  StoreTitle,
  CreateRvcButton,
  TableWrapper,
  RvcTable,
  TableHeader,
  TableCell,
  EmptyMessage,
  TableHeaderCell,
  ExpandStoresButton,
} from "./style";

const Locations = () => {
  const [locations, setLocations] = useState<Locations>();
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(
    new Set()
  );

  // Dialog states
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [rvcDialogOpen, setRvcDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);

  const toggleProperty = (propertyId: string) => {
    setExpandedProperties((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propertyId)) {
        newSet.delete(propertyId);
      } else {
        newSet.add(propertyId);
      }
      return newSet;
    });
  };

  // Dialog handlers
  const handleCreateStore = (property: Property) => {
    setSelectedProperty(property);
    setStoreDialogOpen(true);
  };

  const handleCreateRvc = (property: Property, store: Store) => {
    setSelectedProperty(property);
    setSelectedStore(store);
    setRvcDialogOpen(true);
  };

  const handleStoreSubmit = async (storeData: NewStore) => {
    try {
      const response = await services.post("/admin-locations/store", storeData);
      const typedResponse = response as ApiResponse<Store>;

      if (typedResponse.success) {
        // Add the new store to the appropriate property in local state
        setLocations((prevLocations) => {
          if (!prevLocations) return prevLocations;

          return {
            ...prevLocations,
            properties: prevLocations.properties.map((property) => {
              if (property.id === storeData.property_id) {
                const newStore: Store = {
                  id: typedResponse.data?.id || storeData.id,
                  store_name:
                    typedResponse.data?.store_name || storeData.store_name,
                  property_id:
                    typedResponse.data?.property_id || storeData.property_id,
                  short_name:
                    typedResponse.data?.short_name || storeData.short_name,
                  revenueCenters: [],
                };
                return {
                  ...property,
                  stores: [...property.stores, newStore],
                };
              }
              return property;
            }),
          };
        });
      }

      setStoreDialogOpen(false);
      setSelectedProperty(null);
    } catch (error) {
      console.error("Error creating store:", error);
    }
  };

  const handleRvcSubmit = async (rvcData: NewRevenueCenter) => {
    try {
      const response = await services.post("/admin-locations/rvc", rvcData);
      const typedResponse = response as ApiResponse<RevenueCenter>;

      if (typedResponse.success) {
        // Add the new RVC to the appropriate store in local state
        setLocations((prevLocations) => {
          if (!prevLocations) return prevLocations;

          return {
            ...prevLocations,
            properties: prevLocations.properties.map((property) => {
              if (property.id === rvcData.property_id) {
                return {
                  ...property,
                  stores: property.stores.map((store) => {
                    if (store.id === rvcData.store_id) {
                      const newRvc: RevenueCenter = {
                        id: typedResponse.data?.id || rvcData.id,
                        rvc_name:
                          typedResponse.data?.rvc_name || rvcData.rvc_name,
                        property_id:
                          typedResponse.data?.property_id ||
                          rvcData.property_id,
                        store_id:
                          typedResponse.data?.store_id || rvcData.store_id,
                        rvc_short_name:
                          typedResponse.data?.rvc_short_name ||
                          rvcData.rvc_short_name,
                        location:
                          typedResponse.data?.location || rvcData.location,
                      };
                      return {
                        ...store,
                        revenueCenters: [...store.revenueCenters, newRvc],
                      };
                    }
                    return store;
                  }),
                };
              }
              return property;
            }),
          };
        });
      }

      setRvcDialogOpen(false);
      setSelectedProperty(null);
      setSelectedStore(null);
    } catch (error) {
      console.error("Error creating revenue center:", error);
    }
  };

  useEffect(() => {
    (async () => {
      const locationsList = await getApiResponse("admin-locations");
      if (locationsList.data !== null) {
        setLocations(locationsList.data);
      } else {
        console.log("Unable to set Locations");
      }
    })();
  }, []);

  return (
    <PageWrapper>
      <PageTitle>Administration: Locations</PageTitle>
      {locations && locations.properties ? (
        <div>
          {locations.properties.map((property) => (
            <PropertyContainer key={property.id}>
              {/* Property Header */}
              <HeaderWrapper>
                <PropertyTitle>{property.property_name}</PropertyTitle>
                <CreateStoreButton onClick={() => handleCreateStore(property)}>
                  Create Store
                </CreateStoreButton>
              </HeaderWrapper>

              {/* Stores for this property - conditionally rendered */}
              {expandedProperties.has(property.id) && (
                <div>
                  {property.stores.length > 0 ? (
                    <div>
                      {property.stores.map((store) => (
                        <StoreContainer key={store.id}>
                          {/* Store Header */}
                          <StoreHeaderWrapper>
                            <StoreTitle>
                              Store: {store.store_name} (ID: {store.id})
                            </StoreTitle>
                            <CreateRvcButton
                              onClick={() => handleCreateRvc(property, store)}
                            >
                              Create Revenue Center
                            </CreateRvcButton>
                          </StoreHeaderWrapper>

                          {/* RVCs Table */}
                          {store.revenueCenters.length > 0 ? (
                            <TableWrapper>
                              <RvcTable>
                                <TableHeader>
                                  <tr>
                                    <TableHeaderCell>RVC ID</TableHeaderCell>
                                    <TableHeaderCell>RVC Name</TableHeaderCell>
                                    <TableHeaderCell>
                                      Short Name
                                    </TableHeaderCell>
                                  </tr>
                                </TableHeader>
                                <tbody>
                                  {store.revenueCenters.map((rvc) => (
                                    <tr key={rvc.id}>
                                      <TableCell>{rvc.id}</TableCell>
                                      <TableCell>{rvc.rvc_name}</TableCell>
                                      <TableCell>
                                        {rvc.rvc_short_name}
                                      </TableCell>
                                    </tr>
                                  ))}
                                </tbody>
                              </RvcTable>
                            </TableWrapper>
                          ) : (
                            <EmptyMessage>
                              No revenue centers found for this store
                            </EmptyMessage>
                          )}
                        </StoreContainer>
                      ))}
                    </div>
                  ) : (
                    <EmptyMessage>
                      No stores found for this property
                    </EmptyMessage>
                  )}
                </div>
              )}

              {/* Expand/Collapse Button */}
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <ExpandStoresButton
                  onClick={() => toggleProperty(property.id)}
                  endIcon={
                    expandedProperties.has(property.id) ? (
                      <UpArrowIcon strokeColor="#000" />
                    ) : (
                      <DownArrowIcon strokeColor="#000" />
                    )
                  }
                >
                  {property.stores.length} Stores
                </ExpandStoresButton>
              </div>
            </PropertyContainer>
          ))}
        </div>
      ) : (
        <p>Loading locations data...</p>
      )}

      {/* Dialogs */}
      {selectedProperty && (
        <CreateStoreDialog
          open={storeDialogOpen}
          onClose={() => {
            setStoreDialogOpen(false);
            setSelectedProperty(null);
          }}
          propertyData={selectedProperty}
          onSubmit={handleStoreSubmit}
        />
      )}

      {selectedProperty && selectedStore && (
        <CreateRevenueCenterDialog
          open={rvcDialogOpen}
          onClose={() => {
            setRvcDialogOpen(false);
            setSelectedProperty(null);
            setSelectedStore(null);
          }}
          propertyData={selectedProperty}
          storeData={selectedStore}
          onSubmit={handleRvcSubmit}
        />
      )}
    </PageWrapper>
  );
};

export default Locations;
